import * as B from '@blueprintjs/core';
import React from 'react';
import {Link} from 'react-router-dom';
import {Column, useFlexLayout, useTable} from 'react-table';

import {ApiImageryPurchaseBillingRecord} from 'app/modules/Remote/Project';
import colors from 'app/styles/colors.json';
import * as conversionUtils from 'app/utils/conversionUtils';
import {calculateCost, formatCost} from 'app/utils/costUtils';
import * as imageryUtils from 'app/utils/imageryUtils';
import {makeProjectDashboardUrl, useCurrentOrgIdPrefix} from 'app/utils/routeUtils';

import cs from './ContractHistory.module.scss';
import tableCs from './ImageryContractTable.module.scss';
import ListPriceHeader from './ListPriceHeader';
import PagedTable from './PagedTable';
import {Portfolio, PortfolioSummary, exportPortfolioSummaryCsv} from './utils';

const NUMERIC_COLUMNS = new Set(['scenes', 'acres', 'spentCents']);

const isEarlier = (a: string, b: string) => Date.parse(a) < Date.parse(b);

// Most recently ordered first, then portfolios with no orders by name.
function byMostRecentOrder(a: PortfolioSummary, b: PortfolioSummary) {
  if (a.lastOrder && b.lastOrder) {
    return Date.parse(b.lastOrder) - Date.parse(a.lastOrder);
  }
  if (a.lastOrder || b.lastOrder) {
    return a.lastOrder ? -1 : 1;
  }
  return a.name.localeCompare(b.name);
}

function summarize(
  portfolios: Portfolio[],
  billingRecords: ApiImageryPurchaseBillingRecord[]
): PortfolioSummary[] {
  const summaries = new Map<string, PortfolioSummary>(
    portfolios.map((p) => [
      p.id,
      {...p, firstOrder: null, lastOrder: null, scenes: 0, acres: 0, spentCents: 0},
    ])
  );
  for (const record of billingRecords) {
    // Purchases in portfolios that aren't listed, like archived ones, are left out.
    const summary = summaries.get(record.projectId);
    if (!summary) {
      continue;
    }
    summary.scenes += 1;
    summary.acres += record.billedAcres;
    summary.spentCents += calculateCost(record.billedAcres, record.priceCents);
    if (!summary.firstOrder || isEarlier(record.purchasedAt, summary.firstOrder)) {
      summary.firstOrder = record.purchasedAt;
    }
    if (!summary.lastOrder || isEarlier(summary.lastOrder, record.purchasedAt)) {
      summary.lastOrder = record.purchasedAt;
    }
  }
  return [...summaries.values()].sort(byMostRecentOrder);
}

const formatOrderDate = (orderedAt: string | null) =>
  orderedAt ? imageryUtils.formatDate(orderedAt) : '—';

/**
 * One row per portfolio, summing every purchase in it, whether paid at checkout or billed on a
 * contract's monthly invoice before the org switched.
 */
const PortfolioPurchasesTable: React.FC<{
  portfolios: Portfolio[];
  billingRecords: ApiImageryPurchaseBillingRecord[];
  // Shown left of the download, as ImageryContractTable's heading is.
  heading?: React.ReactNode;
}> = ({portfolios, billingRecords, heading}) => {
  const orgIdPrefix = useCurrentOrgIdPrefix();

  const data = React.useMemo(
    () => summarize(portfolios, billingRecords),
    [portfolios, billingRecords]
  );

  const columns = React.useMemo<Column<PortfolioSummary>[]>(
    () => [
      {
        Header: 'Portfolio',
        id: 'portfolio',
        accessor: 'name',
        Cell: ({row: {original: portfolio}}) => (
          <Link
            className={cs.portfolioCell}
            to={makeProjectDashboardUrl(orgIdPrefix, portfolio.id)}
          >
            {portfolio.name}
          </Link>
        ),
        width: 140,
      },
      {
        Header: 'First order',
        accessor: 'firstOrder',
        Cell: ({cell}) => formatOrderDate(cell.value as string | null),
        width: 60,
      },
      {
        Header: 'Last order',
        accessor: 'lastOrder',
        Cell: ({cell}) => formatOrderDate(cell.value as string | null),
        width: 60,
      },
      {Header: 'Scenes', accessor: 'scenes', width: 40},
      {
        Header: 'Acres',
        accessor: 'acres',
        Cell: ({cell}) => conversionUtils.numberWithCommas((cell.value as number).toString()),
        width: 50,
      },
      {
        Header: <ListPriceHeader label="Total spent" />,
        accessor: 'spentCents',
        Cell: ({cell}) => formatCost(cell.value as number),
        width: 60,
      },
    ],
    [orgIdPrefix]
  );

  const instance = useTable({columns, data}, useFlexLayout);

  if (instance.rows.length === 0) {
    return <B.NonIdealState icon="folder-close" title="No portfolios yet" />;
  }

  return (
    <>
      <div className={tableCs.tableMeta}>
        <div className={tableCs.tableMetaHeader}>
          {heading && <div className={tableCs.tableHeading}>{heading}</div>}
          <B.Button
            small
            minimal
            title="Download CSV of purchases by portfolio"
            aria-label="Download CSV of purchases by portfolio"
            icon={<B.Icon icon="download" color={colors.darkestGray} size={14} />}
            onClick={() => exportPortfolioSummaryCsv(data)}
          />
        </div>
      </div>
      <PagedTable instance={instance} numericColumns={NUMERIC_COLUMNS} />
    </>
  );
};

export default PortfolioPurchasesTable;
